import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import { errorHandler } from "@/Services/errorHandler";
import TimeManager from "@/Services/TimeManager";
import saleModule from "./modules/sales-module";
import stockModule from "./modules/stock-module";
import supervisorModule from "./modules/supervisor-module";
import financeModule from "./modules/finance-module";
import invoicesModule from "./modules/invoices-module";
import timeModule from "./modules/time-module";
import accountsModule from "./modules/accounts-module";
import dashboardsModule from "./modules/dashboards-module";
import pdfModule from './modules/pdf';
import debtorsActivity from "./modules/reports-debtors-activity";
import currencyModule from "@/store/modules/currency-module";
import waiterModule from "./modules/waiter-module";
import paymentsModule from "./modules/payments-module";
import warningsReportsModule from "./modules/warnings-reports-module";

Vue.use(Vuex);

//vuex persist
import VuexPersistence from 'vuex-persist';
import {idbTransactionalStorage, restoreState, saveState} from '@/Services/idbStorage';

const getDefaultState = () => ({
  loading: false,
  isUpdateAvailable: false,
  franchiseUser: {},
  stores: [],
  storeUsers: [],
  selected_store: {},
  timeFrame: TimeManager.getThisMonth(),
  activeTimeTab: 5,
  printTimeFrame: { printFromDatetime: null, printToDatetime: null },
});

const vuexLocal = new VuexPersistence({
  storage: idbTransactionalStorage,
  asyncStorage: true,
  key: "vuex",
  restoreState,
  saveState
});

const store = new Vuex.Store({
  state: getDefaultState(),
  getters: {
    getUpdateAvailability: (state) => state.isUpdateAvailable,
    getLoadingState: (state) => state.loading,
    getFranchiseUser: (state) => state.franchiseUser,
    getStores: (state) => state.stores,
    getStore: (state) => state.selected_store,
    getStoreUsers: (state) => state.storeUsers,
    getTimeFrame: (state) => state.timeFrame,
    getActiveTimeTab: (state) => state.activeTimeTab,
    getPrintTimeFrame: (state) => state.printTimeFrame,
  },
  mutations: {
    setActiveTimeTab: (state, payload) => {
      state.activeTimeTab = payload;
    },
    setTimeFrame: (state, payload) => {
      console.log("setTimeFrame payload:" + payload.from, payload.to);
      // Trust normalized {from,to} with yyyy-MM-dd HH:mm:ss when provided.
      // Only compute auxiliary startTime/endTime when from/to are DATE-ONLY (YYYY-MM-DD).
      const enriched = { ...payload };
      try {
        const isDateOnly = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
        if (payload && payload.from && payload.to && isDateOnly(payload.from) && isDateOnly(payload.to)) {
          const [fy, fm, fd] = payload.from.split('-').map(Number);
          const [ty, tm, td] = payload.to.split('-').map(Number);
          const startLocal = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
          // Treat 'to' as exclusive upper bound only if it is AFTER 'from'; convert to inclusive previous day end
          const toLocalExclusive = new Date(ty, tm - 1, td, 0, 0, 0, 0);
          const fromLocal = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
          let endLocal;
          if (toLocalExclusive.getTime() > fromLocal.getTime()) {
            // to is after from - treat as exclusive (next day midnight → previous day end)
            endLocal = new Date(toLocalExclusive.getTime() - 1);
          } else {
            // to equals from - same day selected, use end of that day
            endLocal = new Date(toLocalExclusive.getFullYear(), toLocalExclusive.getMonth(), toLocalExclusive.getDate(), 23, 59, 59, 999);
          }
          enriched.startTime = startLocal.toISOString();
          enriched.endTime = endLocal.toISOString();
        }
      } catch (e) {
        console.warn('Failed to enrich timeFrame with times', e);
      }
      state.timeFrame = enriched;
    },
    setLoadingState: (state, payload) => (state.loading = payload),
    setUpdateAvailability: (state, payload) =>
      (state.isUpdateAvailable = payload),
    setFranchiseUser: (state, payload) => {
      state.franchiseUser = payload;
      let encrypted = window.btoa(JSON.stringify(payload));
      localStorage.setItem("franchiseUser", encrypted);
    },
    setStores: (state, payload) => {
      state.stores = payload;
      localStorage.setItem("stores", JSON.stringify(payload));
    },
    setStore: (state, payload) => {
      state.selected_store = payload;
      localStorage.setItem("selected_store", JSON.stringify(payload));
    },
    setStoreUsers: (state, payload) => {
      state.storeUsers = payload;
      localStorage.setItem("storeUsers", JSON.stringify(payload));
    },
    useDefaultStore: (state) => {
      let stores = JSON.parse(localStorage.getItem("stores"));
      let defaultStore = {
        StoreName: "All",
        StoreDB: null,
        Group: stores[0].Group,
      };
      state.selected_store = defaultStore;
      localStorage.setItem("selected_store", JSON.stringify(defaultStore));
    },
    resetRootState: (state) => {
      const fresh = getDefaultState();
      Object.keys(fresh).forEach((key) => {
        state[key] = fresh[key];
      });
    },
    setPrintTimeFrame: (state, payload) => {
      state.printTimeFrame = payload;
    },
  },
  actions: {
    updateActiveTimeTab({ commit }, tab) {
      commit("setActiveTimeTab", tab);
    },
    async franchiseLogin(context, payload) {
      try {
        const { data } = await axios.post(
          backOfficeTomcatApi + "mobileUser/login",
          payload
        );
        $utils.hideLoading();
        context.commit("setFranchiseUser", payload);
        context.commit("setStores", data);
        if (data.length > 1) {
          let defaultStore = {
            StoreName: "All",
            StoreDB: null,
            Group: data[0].Group,
          };
          context.commit("setStore", defaultStore);
        } else {
          let defaultStore = {
            StoreName: data[0].StoreName,
            StoreDB: data[0].StoreDB,
            Group: data[0].Group,
          };
        }
      } catch (err) {
        errorHandler.tomcatError(err);
      }
    },
    async getAllUsers(context) {
      let store = JSON.parse(localStorage.getItem(`selected_store`));
      let storeDB = store.StoreDB;
      console.log("DB Name", storeDB);

      let headers = {
        DB: storeDB ? window.btoa(storeDB) : null,
      };
      if (storeDB !== null) {
        try {
          return await axios
            .get(reportsApi + `users/getAll`, {
              headers,
            })
            .then(({ data }) => {
              context.commit("setStoreUsers", data);
              return data;
            })
            .catch((err) => errorHandler.tomcatError(err));
        } catch (err) {
          errorHandler.tomcatError(err);
        }
      }
    },
  },
  modules: {
    saleModule,
    stockModule,
    timeModule,
    supervisorModule,
    invoicesModule,
    accountsModule,
    dashboardsModule,
    financeModule,
    currencyModule,
    waiterModule,
    paymentsModule,
    debtorsActivity,
    warningsReportsModule,
    pdf: pdfModule,
  },
  plugins: [
    vuexLocal.plugin,
  ],
});

export default store;
